import React, { useState, useEffect, ComponentProps } from 'react';
import * as ReactDOM from 'react-dom';
import ViewportProvider from '@cobalt/react-viewport-provider';
import PortalProvider from '@cobalt/react-portal-provider';

import { Heading } from '@cobalt/react-typography';
import Flex from '@cobalt/react-flex';
import Button from '@cobalt/react-button';
import Modal from './Modal';
import { Text } from '@cobalt/react-typography';
import Icon from '@cobalt/react-icon';
import Spinner from '@cobalt/react-spinner';
import { v4 as uuidv4 } from 'uuid';

export interface TemplateConfiglProps {
  onOk?: (...args: any[]) => void | Promise<any>;
  onCancel?: (...args: any[]) => void | Promise<any>;
  size?: ComponentProps<typeof Modal>['size'];
  title?: string;
  content?: string;
  description?: string;
  okText?: string;
  cancelText?: string;
  okType?: 'primary' | 'secondary' | 'danger' | 'warning' | 'success' | 'feature' | 'mono-dark' | 'mono-light';
  cancelType?: 'primary' | 'secondary' | 'danger' | 'warning' | 'success' | 'feature' | 'mono-dark' | 'mono-light';
  icon?: React.ReactNode;
  closable?: boolean;
  type?: 'info' | 'success' | 'error' | 'warn' | 'warning' | 'confirm';
  okCancel?: boolean;
}

export type ModalStaticFunctions = Record<NonNullable<TemplateConfiglProps['type']>, ModalFunc>;

export type ModalFunc = (props: TemplateConfiglProps, wrapperElm?: HTMLElement) => void;

let preTmpId: string = '';

export function baseConfirm({
  size = 'small',
  icon = '',
  okText = 'Confirm',
  cancelText = 'Cancel',
  title = '',
  content = '',
  description = '',
  onCancel,
  onOk,
  okType = 'primary',
  cancelType = 'secondary',
  okCancel,
  closable = false,
}: TemplateConfiglProps) {
  if (document.getElementById(preTmpId)) return;
  preTmpId = uuidv4();

  let wrapDiv = document.createElement('div');
  wrapDiv.setAttribute('id', preTmpId);

  let currentWrapperElm = document.getElementById('co-portal-container') as HTMLElement;

  currentWrapperElm.appendChild(wrapDiv);

  const ConfirmModal = () => {
    const [loading, setLoaing] = useState<boolean>(false);
    const [visible, setVisible] = useState<boolean>(false);

    useEffect(() => {
      setTimeout(() => {
        setVisible(true);
      }, 1000 / 60);
    }, []);

    const processBeforeDestroy = async (fn: TemplateConfiglProps['onOk']) => {
      const result = typeof fn === 'function' && fn();
      setLoaing(true);

      if (result instanceof Promise) {
        await result;
      }
    };

    const destroy = async (operationType: TemplateConfiglProps['onOk' | 'onCancel']) => {
      if (wrapDiv.parentNode) {
        try {
          preTmpId = '';
          await processBeforeDestroy(operationType);
          setVisible(false);
          setTimeout(() => {
            wrapDiv?.parentNode?.removeChild(wrapDiv);
          }, 500);
        } catch {
        } finally {
          setLoaing(false);
        }
      }
    };

    return (
      <Modal
        visible={visible}
        size={size}
        closable={closable}
        onCancel={() => destroy(onCancel)}
        footer={
          <Flex width="100%" alignX="center" gap={2}>
            {okCancel ? (
              [
                <Button key={1} type={cancelType} size={'medium'} onClick={() => destroy(onCancel)}>
                  {cancelText}
                </Button>,
                <Button key={2} type={okType} size={'medium'} disabled={loading} onClick={() => destroy(onOk)}>
                  <Flex alignY="center">
                    {loading && <Spinner className="button-loading" size="micro" />}
                    {okText}
                  </Flex>
                </Button>,
              ]
            ) : (
              <Button type={okType} size={'medium'} disabled={loading} onClick={() => destroy(onOk)}>
                <Flex alignY="center">
                  {loading && <Spinner className="button-loading" size="micro" />}
                  {okText}
                </Flex>
              </Button>
            )}
          </Flex>
        }
      >
        <Flex direction="column" alignX="center" padding={2} width="100%">
          <Flex alignX="center"> {icon} </Flex>
          <Flex alignX="center" paddingX={2} paddingY={3}>
            <Heading level="2" style={{ textAlign: 'center' }}>
              {title}
            </Heading>
          </Flex>
          <Flex alignX="center" paddingX={2} direction="column">
            <Text weight="regular" size="medium" style={{ textAlign: 'center' }}>
              {content}
            </Text>

            {!!description && (
              <Text weight="regular" size="medium" style={{ textAlign: 'center' }}>
                {description}
              </Text>
            )}
          </Flex>
        </Flex>
      </Modal>
    );
  };

  function render() {
    ReactDOM.render(
      <ViewportProvider>
        <PortalProvider>
          <ConfirmModal />
        </PortalProvider>
      </ViewportProvider>,
      wrapDiv,
    );
  }

  render();
}

export function withConfirm(props: TemplateConfiglProps): TemplateConfiglProps {
  return {
    icon: <Icon name={'warning_outline'} size="large" color="var(--yellow-600)" />,
    okCancel: true,
    ...props,
    type: 'confirm',
  };
}

export function withError(props: TemplateConfiglProps): TemplateConfiglProps {
  return {
    icon: <Icon name={'error_outline'} size="large" color="var(--red-700)" />,
    okCancel: false,
    okText: 'Ok',
    ...props,
    type: 'error',
  };
}

export function withInfo(props: TemplateConfiglProps): TemplateConfiglProps {
  return {
    icon: <Icon name={'info_outline'} size="large" color="var(--primary-700)" />,
    okCancel: false,
    okText: 'Ok',
    ...props,
    type: 'info',
  };
}

export function withSuccess(props: TemplateConfiglProps): TemplateConfiglProps {
  return {
    icon: <Icon name={'check_circle_outline'} size="large" color="var(--success-700)" />,
    okCancel: false,
    okText: 'Ok',
    ...props,
    type: 'success',
  };
}

export function withWarning(props: TemplateConfiglProps): TemplateConfiglProps {
  return {
    icon: <Icon name={'warning_outline'} size="large" color="var(--yellow-700)" />,
    okCancel: false,
    okText: 'Ok',
    ...props,
    type: 'warning',
  };
}
